import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download } from "lucide-react";
import { isStandalone } from "@/lib/pwa";

/**
 * "Install App" entry for the user/profile menu.
 *
 * Behaviour:
 *   • Standalone (already installed) → renders nothing
 *   • Native prompt captured (Android/Desktop Chrome/Edge) → clicking calls
 *     `deferredPrompt.prompt()` immediately
 *   • Everything else (iOS Safari / iOS non-Safari / desktop without prompt)
 *     → dispatches `vu:force-show-install`, which the existing
 *     `PWAInstallPrompt` component uses to open the right branded card
 *     (Safari A2HS instructions / "open in Safari" hint / desktop menu guide).
 *
 * Matches the sidebar's Sign-out button styling — 16px icon, muted text,
 * magenta hover — so it feels like a native part of the user menu.
 */
export default function InstallAppMenuItem({ isCollapsed = false, onNav }) {
    const { t } = useTranslation();
    const [supported, setSupported] = useState(false);
    const [hasDeferred, setHasDeferred] = useState(
        typeof window !== "undefined" && !!window.__vuDeferredInstall
    );

    useEffect(() => {
        if (typeof window === "undefined") return;
        if (isStandalone()) {
            setSupported(false);
            return;
        }
        setSupported(true);

        const onInstallable = () => setHasDeferred(true);
        const onInstalled = () => {
            setHasDeferred(false);
            setSupported(false);
        };
        window.addEventListener("vu:installable", onInstallable);
        window.addEventListener("vu:installed", onInstalled);
        return () => {
            window.removeEventListener("vu:installable", onInstallable);
            window.removeEventListener("vu:installed", onInstalled);
        };
    }, []);

    if (!supported) return null;

    const label = t("install.menuLabel");

    const handleClick = async () => {
        if (onNav) onNav();  // close mobile drawer if open
        const evt = window.__vuDeferredInstall;
        if (evt) {
            try {
                evt.prompt();
                const choice = await evt.userChoice;
                if (choice?.outcome === "accepted") {
                    window.dispatchEvent(new CustomEvent("vu:installed"));
                }
            } catch { /* ignore */ }
            window.__vuDeferredInstall = null;
            setHasDeferred(false);
            return;
        }
        // No cached prompt — surface the branded card with the right guidance
        window.dispatchEvent(new CustomEvent("vu:force-show-install"));
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            data-testid="menu-install-app"
            title={isCollapsed ? label : undefined}
            aria-label={label}
            className={`relative flex items-center ${isCollapsed ? "justify-center w-full" : "gap-2"} text-sm text-[var(--text-ink)]/70 hover:text-[var(--brand-magenta)] px-2 py-1.5 transition-colors`}
        >
            <Download size={16} className="shrink-0" />
            {!isCollapsed && (
                <span className="inline-flex items-center gap-1.5">
                    <span aria-hidden="true">📱</span>
                    {label}
                    {hasDeferred && (
                        <span
                            aria-hidden="true"
                            className="ml-0.5 h-1.5 w-1.5 rounded-full bg-[var(--brand-magenta)] animate-pulse"
                            title={t("install.readyToInstall")}
                        />
                    )}
                </span>
            )}
            {isCollapsed && hasDeferred && (
                <span
                    aria-hidden="true"
                    className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-[var(--brand-magenta)] animate-pulse"
                />
            )}
        </button>
    );
}
